import { useState } from 'react'
import { Form, Formik } from 'formik'
import NiceModal, { useModal } from '@ebay/nice-modal-react'

import { Button, Modal, useShowMessage, useUpdateReport } from '@pepper/ui'

import { useSelectedCompanyId } from 'utils/functions/misc'
import { getFieldComponent } from 'components/FormFields/FormFields'
import {
  MetricFormValues,
  metricFields,
  metricFormStep,
  metricsValidationSchema,
} from './schema'

import styles from './EditMetricsForm.module.css'

import type { ResearchReport } from '@pepper/utils/src/types/seo'

interface Props {
  reportId: string
  initialFormValues: MetricFormValues
}

function EditMetricsForm({ reportId, initialFormValues }: Props) {
  const companyId = useSelectedCompanyId()
  const modal = useModal()
  const showMessage = useShowMessage()

  const [formValues, setFormValues] =
    useState<MetricFormValues>(initialFormValues)
  // 1st step: editing content length which will scale other metrics
  // 2nd step: disable content length and allow editing other metrics
  const [step, setStep] = useState<1 | 2>(1)

  const updateReport = useUpdateReport()

  // scale metrics based on content_length `scaling_factor=new_length/oldLength`
  const refreshMetrics = (values: MetricFormValues) => {
    const scalingFactor =
      values.content_length / initialFormValues.content_length

    setFormValues({
      ...values,
      hx: Math.floor(initialFormValues.hx * scalingFactor),
      p: Math.floor(initialFormValues.p * scalingFactor),
      img: Math.floor(initialFormValues.img * scalingFactor),
      a: Math.floor(initialFormValues.a * scalingFactor),
    })
  }

  const handleSubmit = (values: MetricFormValues) => {
    if (step === 1) {
      refreshMetrics(values)
      setStep(2)
      return
    }

    const modifiedReport: Partial<ResearchReport['document']> = {
      page_length: { min: +values.content_length, max: +values.content_length },
      element_counts: {
        a: { min: +values.a, max: +values.a, mean: +values.a },
        img: { min: +values.img, max: +values.img, mean: +values.img },
        p: { min: +values.p, max: +values.p, mean: +values.p },
        hx: { min: +values.hx, max: +values.hx, mean: +values.hx },
      },
    }

    updateReport.mutate(
      {
        companyId,
        reportId,
        body: {
          document: modifiedReport,
          length_scale_factor:
            values.content_length / initialFormValues.content_length,
        },
      },
      {
        onSuccess: () => {
          showMessage({ type: 'success', message: 'Report updated' })
          modal.remove()
        },
        onError: error => {
          if (error instanceof Error) {
            showMessage({ type: 'error', message: error.message })
          }
        },
      }
    )
  }

  return (
    <Modal
      title={metricFormStep[step].title}
      subheader={metricFormStep[step].description}
      visible={modal.visible}
      footer={null}
      width={800}
      className={styles.modal}
    >
      <Formik<MetricFormValues>
        initialValues={formValues}
        enableReinitialize={true}
        validationSchema={metricsValidationSchema}
        onSubmit={handleSubmit}
      >
        {(formikProps): JSX.Element => (
          <Form>
            <div className={styles.formContainer}>
              {metricFields.map(field => {
                const disabledField =
                  (field.name !== 'content_length' && step === 1) ||
                  (field.name === 'content_length' && step === 2)
                return (
                  <div key={field.name} className={styles.field}>
                    {getFieldComponent({
                      ...field,
                      actions: formikProps,
                      fieldProps: { disabled: disabledField },
                    })}
                  </div>
                )
              })}
            </div>

            <div className={styles.ctaContainer}>
              <Button type="link" onClick={modal.remove}>
                Cancel
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                disabled={updateReport.isLoading}
                loading={updateReport.isLoading}
              >
                {step === 1 ? 'Set Content Length' : 'Recalculate Insights'}
              </Button>
            </div>
          </Form>
        )}
      </Formik>
    </Modal>
  )
}

export default NiceModal.create<Props>(EditMetricsForm)
